import {
  Children,
  cloneElement,
  isValidElement,
  ReactElement,
  useId,
  useRef,
} from 'react';
import { BaseProps, HTMLElementWithAnchor, Popover2025 } from './Popover2025';
import { useDelayedHover } from './useDelayedHover';
import { invariant } from '@ardoq/invariant';
import { StyledPopoverContainer } from './StyledPopoverContainer';

/**
 * As we will be attaching mouse events to the child element,
 * we need to ensure that the child can accept those props. And that we aren't
 * overwriting any existing handlers.
 */
export type ChildProps = {
  ref?: React.RefObject<HTMLElementWithAnchor | null>;
  onMouseEnter?: (e: React.MouseEvent) => void;
  onMouseLeave?: (e: React.MouseEvent) => void;
  onFocus?: (e: React.FocusEvent) => void;
  onBlur?: (e: React.FocusEvent) => void;
};

type WithUncontrolledPopoverProps = Partial<BaseProps> & {
  /** Popover content */
  content:
    | ReactElement<ChildProps>
    | null
    | (() => ReactElement<ChildProps> | null);

  /** Anchor element */
  children: ReactElement<ChildProps>;

  /** Whether hovering over the popover keeps it open. When false, the popover closes as soon as the mouse leaves the anchor. Defaults to true. */
  keepOpenOnPopoverHover?: boolean;

  /** Whether keyboard focus on the anchor also opens the popover. Defaults to false. */
  openOnFocus?: boolean;
};

/**
 * A wrapper component that automatically shows a popover on hover.
 *
 * Unlike the controlled Popover2025, this component manages its own open/close state
 * and responds to mouse enter/leave events on the trigger element, and
 * optionally to focus/blur.
 *
 */
export const WithUncontrolledPopover = ({
  content,
  children,
  placement = { side: 'bottom', overflow: 'center' },
  keepOpenOnPopoverHover = true,
  openOnFocus = false,
  ...popoverProps
}: WithUncontrolledPopoverProps): ReactElement => {
  const { isOpen, handleMouseEnter, handleMouseLeave, handleClose } =
    useDelayedHover();
  const locallyCreatedRef = useRef<HTMLElementWithAnchor | null>(null);
  const id = useId();

  const child = Children.only(children);

  invariant(
    isValidElement(child),
    'WithUncontrolledPopover expects a single React element as its child.'
  );

  const childProps = child.props;
  const anchorRef = childProps.ref ?? locallyCreatedRef;

  const triggerElement = cloneElement(child, {
    ref: anchorRef,
    onMouseEnter: (e: React.MouseEvent) => {
      handleMouseEnter();
      childProps.onMouseEnter?.(e);
    },
    onMouseLeave: (e: React.MouseEvent) => {
      handleMouseLeave();
      childProps.onMouseLeave?.(e);
    },
    ...(openOnFocus && {
      onFocus: (e: React.FocusEvent) => {
        handleMouseEnter();
        childProps.onFocus?.(e);
      },
      onBlur: (e: React.FocusEvent) => {
        handleMouseLeave();
        childProps.onBlur?.(e);
      },
    }),
  });

  const popoverContent = isOpen
    ? typeof content === 'function'
      ? content()
      : content
    : null;

  return (
    <>
      {triggerElement}
      <Popover2025
        isOpen={isOpen && popoverContent !== null}
        onClose={handleClose}
        id={id}
        anchorRef={anchorRef}
        placement={placement}
        {...popoverProps}
      >
        <StyledPopoverContainer
          isOpen={isOpen}
          preferredSide={placement.side}
          anchorId={id}
          anchorRef={anchorRef}
          onMouseEnter={keepOpenOnPopoverHover ? handleMouseEnter : undefined}
          onMouseLeave={keepOpenOnPopoverHover ? handleMouseLeave : undefined}
          disablePointerEvents={!keepOpenOnPopoverHover}
        >
          {popoverContent}
        </StyledPopoverContainer>
      </Popover2025>
    </>
  );
};
